// Centralized copy & data for the Shreedhan landing page.
import {
  Wallet, Coins, ShieldCheck, Globe2, Sparkles, TrendingUp, Vault,
  ArrowLeftRight, PiggyBank, Users, Gift, Trophy, Flame, Medal,
  Leaf, HeartHandshake, Landmark, BadgeCheck, LineChart, Lock,
} from "lucide-react";

export const NAV_LINKS = [
  { label: "How it works", href: "#how-it-works" },
  { label: "For NRIs", href: "#nri-stack" },
  { label: "Features", href: "#core-products" },
  { label: "Pricing", href: "#pricing" },
  { label: "Trust & Security", href: "#fees" },
];

export const LANGUAGES = ["EN", "हिं", "ਪੰ", "த"];

export const TRUST_LOGOS = [
  { name: "SEBI", sub: "Regulated" },
  { name: "MMTC-PAMP", sub: "Refiner" },
  { name: "RBI KYC", sub: "Compliant" },
  { name: "BIS Hallmark", sub: "999.9 Fine" },
  { name: "NSDL", sub: "Custody" },
];

export const INSIGHTS = [
  {
    icon: Wallet,
    tag: "The leak",
    title: "Cash quietly gets spent",
    body: "Money sitting in a savings account loses to inflation and the temptation of everyday spends. By year-end, little is left to show.",
  },
  {
    icon: TrendingUp,
    tag: "The truth",
    title: "Gold appreciates & endures",
    body: "For generations, Indian households have trusted gold to preserve and grow wealth — through every market, every border, every celebration.",
  },
  {
    icon: Globe2,
    tag: "The gap",
    title: "SGBs discontinued for NRIs",
    body: "With Sovereign Gold Bonds paused and NRIs excluded, the diaspora lost a clean way to hold Indian gold. Shreedhan fills that gap.",
  },
];

export const CORE_PRODUCTS = [
  {
    icon: Coins,
    name: "Shreedhan Gold",
    tagline: "24K digital gold, from ₹10",
    body: "Buy, hold and sell 999.9 fine gold, fully allocated and vaulted in your name. Start with the price of a chai.",
  },
  {
    icon: PiggyBank,
    name: "Shreedhan SIP",
    tagline: "Automated gold savings",
    body: "Set it once. A small amount converts to gold on your schedule — the disciplined habit that builds a golden corpus.",
  },
  {
    icon: Sparkles,
    name: "Shreedhan Coach",
    tagline: "AI that guides your gold",
    body: "Gentle, personalised nudges that time your buys, flag opportunities and keep your goals on track.",
  },
  {
    icon: Globe2,
    name: "Shreedhan Global",
    tagline: "Borderless gold for NRIs",
    body: "Remit, gift and invest in Indian gold from anywhere — multi-currency, KYC-light, family-friendly.",
  },
];

export const STEPS = [
  { icon: ArrowLeftRight, step: "01", title: "Fund", body: "Add money via UPI, card or international transfer — INR or your local currency." },
  { icon: Coins, step: "02", title: "Convert", body: "Your money instantly buys 999.9 fine gold at a live, transparent rate." },
  { icon: Vault, step: "03", title: "Vault", body: "Gold is insured, allocated and stored in a BIS-hallmarked vault under your name." },
];

export const COACH_NUDGES = [
  { tone: "opportunity", text: "Gold dipped 1.8% today. A good moment to top up your Diwali goal — want to add ₹500?" },
  { tone: "streak", text: "You're on a 12-week saving streak! One more week unlocks the Gold tier. Keep it glowing." },
  { tone: "goal", text: "At ₹2,000/month you'll reach your daughter's wedding fund 4 months early. Nudge it up?" },
];

export const NRI_PRODUCTS = [
  { icon: ArrowLeftRight, name: "Gold Remittance", pitch: "Send value home as gold, not just rupees — it holds its worth." },
  { icon: PiggyBank, name: "NRI Gold SIP", pitch: "Automate Indian gold savings from the UK, US, UAE and beyond." },
  { icon: Users, name: "Gold for Family", pitch: "Gift gold to parents & children in India with a tap, fully KYC-compliant." },
  { icon: Wallet, name: "Gold Wallet", pitch: "Hold, convert and spend across currencies — one borderless wallet." },
];

export const GAMIFICATION_TIERS = [
  { name: "Bronze", color: "#C08457", pct: "0–25%" },
  { name: "Silver", color: "#B8BCC2", pct: "25–50%" },
  { name: "Gold", color: "#D4AF37", pct: "50–80%" },
  { name: "Platinum", color: "#9BA7B4", pct: "80–100%" },
];

export const GAME_FEATURES = [
  { icon: Flame, title: "Streaks", body: "Save every week, keep your flame alive." },
  { icon: Medal, title: "Badges", body: "Earn Festival Saver, Family First & more." },
  { icon: Trophy, title: "Leaderboard", body: "Climb your circle's savings ranks." },
];

export const DIASPORA = [
  { flag: "🇬🇧", country: "United Kingdom", line: "Send Diwali gold home from London before you sleep." },
  { flag: "🇺🇸", country: "United States", line: "Build an Indian gold corpus in USD from anywhere." },
  { flag: "🇦🇪", country: "UAE", line: "Zero-friction gold savings for the Gulf's Indian families." },
  { flag: "🇨🇦", country: "Canada & Australia", line: "Gift gold to grandparents across the time zones." },
];

export const TESTIMONIALS = [
  { name: "Ananya R.", role: "Software Lead, Bengaluru", quote: "Shreedhan turned my messy savings habit into a golden one. The streaks make it weirdly addictive — in a good way." },
  { name: "Priya & Dev", role: "NRI couple, London", quote: "We finally have a clean way to send gold to our parents in Pune. It feels like carrying tradition, not just money." },
  { name: "Meera K.", role: "Entrepreneur, Dubai", quote: "The AI Coach nudged me to buy on a dip before my daughter's wedding fund goal. Small nudges, real difference." },
];

export const FEE_ROWS = [
  { label: "Gold buy spread", value: "0.0%", note: "Live MMTC-PAMP rate" },
  { label: "Platform fee", value: "₹0", note: "On Core plan" },
  { label: "Vault & insurance", value: "0.4% / yr", note: "Fully allocated" },
  { label: "Withdrawal to bank", value: "₹0", note: "Standard payout" },
];

export const PRICING = [
  {
    name: "Core", price: "Free", cadence: "forever",
    features: ["Buy gold from ₹10", "Gold SIP & round-ups", "Basic AI Coach", "Streaks & badges"],
    highlight: false, cta: "Start free",
  },
  {
    name: "NRI Global", price: "₹199", cadence: "/mo",
    features: ["Everything in Core", "Multi-currency wallet", "Gold remittance", "Family gifting", "Priority KYC"],
    highlight: false, cta: "Go global",
  },
  {
    name: "Vault+", price: "₹499", cadence: "/mo",
    features: ["Everything in NRI Global", "Advanced AI Coach", "What-if simulator", "Reduced vault fee", "Concierge support"],
    highlight: true, cta: "Get Vault+",
  },
  {
    name: "For Business", price: "Custom", cadence: "B2B",
    features: ["Gold-linked rewards", "Employee gold SIPs", "API & payouts", "Bulk gifting", "Dedicated manager"],
    highlight: false, cta: "Talk to us",
  },
];

export const MANIFESTO = [
  { n: "01", head: "Rooted in tradition", body: "Gold isn't an asset to us — it's memory, ceremony and security passed down through generations of Indian women." },
  { n: "02", head: "Powered by technology", body: "We turn that timeless instinct into a few taps: fractional, insured, vaulted, and coached by intelligent guidance." },
  { n: "03", head: "Borderless by design", body: "From Bengaluru to Birmingham, wealth should move with families — not stop at a border or a discontinued bond." },
];

export const ESG = {
  stat: "₹1 in every ₹4",
  statLabel: "of new gold savings on Shreedhan is started by a woman investing for the first time.",
  points: [
    { icon: HeartHandshake, title: "Women's wealth first", body: "Built for the women who hold Indian households together — and their financial independence." },
    { icon: BadgeCheck, title: "Responsibly sourced", body: "Only LBMA & BIS-hallmarked, conflict-free refined gold enters the vault." },
    { icon: Leaf, title: "Gold that gives back", body: "A slice of every SIP funds tree-planting & carbon offsets — wealth that grows greener." },
  ],
};

export const TRUST_BADGES = [
  { icon: ShieldCheck, label: "SEBI regulated" },
  { icon: Landmark, label: "NSDL custody" },
  { icon: Lock, label: "Bank-grade security" },
  { icon: LineChart, label: "Live audited vault" },
];
